/* ═══════════════════════════════════════════════════════════════
   useTableSync — Targeted live-refresh for a single component.

   Replaces the old pattern:

     useEffect(() => {
       const h = () => load();
       window.addEventListener('specforma-sync-complete', h);
       return () => window.removeEventListener('specforma-sync-complete', h);
     }, []);

   with:

     useTableSync(['roles'], load);

   Why this matters: cloudCache fires `specforma-sync-complete` with
   `detail.tables = [...]` listing only the tables that actually changed.
   The old pattern blindly re-fetched on EVERY event, causing a save in
   one tab to trigger 9 unrelated refetches across the AdminPage. With
   table filtering, only the components whose data changed re-fetch.
   This was a major source of API rate-limit pressure pre-v2.11.8.

   Pass `null` to opt-into the legacy "always refetch" behavior.
   ═══════════════════════════════════════════════════════════════ */

import { useEffect } from 'react';

export function useTableSync(tables: string[] | null, onChange: () => void): void {
  useEffect(() => {
    const handler = (e: Event) => {
      if (tables === null) {
        onChange();
        return;
      }
      const detail = (e as CustomEvent).detail as { tables?: string[] } | undefined;
      const changed = detail?.tables;
      if (!changed || changed.length === 0) {
        // No detail = unknown change scope; refetch defensively.
        onChange();
        return;
      }
      if (changed.some((t) => tables.includes(t))) {
        onChange();
      }
    };
    window.addEventListener('specforma-sync-complete', handler);
    return () => window.removeEventListener('specforma-sync-complete', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tables ? tables.join(',') : '__null__']);
}
